import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  WorkOrderComplement,
  workorderComplementColor,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Button, Card, Col, Form, Row, Select, Table, Typography } from 'antd';
import { useEffect } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { InvoiceType } from '@/core/domain/invoice/invoices/invoices.enums';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useIntl } from 'react-intl';
import { getWorkorderComplementNumber } from '@/utils/functions/getWorkorderComplementNumber';
import { useCouponCompanyConfigDetail } from '@/core/domain/coupon/company/company.hooks';

export interface WorkOrderInvoiceFormValues {
  companyId: number;
  invoiceModelType: number;
  complements: (WorkOrderComplement & { checked: boolean })[];
}

export const invoiceValuesModelsOptions = [
  { value: 55, label: '55 - NF-e', invoiceType: InvoiceType.NFE },
  { value: 65, label: '65 - NFC-e', invoiceType: InvoiceType.NFC },
  { value: 59, label: '59 - CF-e', invoiceType: InvoiceType.CFE },
];

interface Props {
  onSubmit(values: WorkOrderInvoiceFormValues): void;
  onChange(): void;
}

const WorkOrderInvoiceComplements: React.FC<Props> = ({
  onSubmit,
  onChange,
}) => {
  const {
    workorder: { data: workorder },
    company: {
      invoiceSettingsByCompany: {
        data: invoiceSettingsByCompany,
        isLoading: invoiceSettingsByCompanyIsLoading,
      },
    },
    setComplementCompanyId,
  } = useWorkorderDetailContext();

  const [form] = Form.useForm<WorkOrderInvoiceFormValues>();
  const fieldRuleRequired = useDefaultEmptyFieldError();
  const { data: matrix } = useMatrix();
  const complementsField = Form.useWatch('complements', form);
  const companyIdField = Form.useWatch('companyId', form);

  const intl = useIntl();

  const couponCompanyConfig = useCouponCompanyConfigDetail(companyIdField);

  const hasCouponConfigActive =
    !!couponCompanyConfig.data?.defaultTaxConfig.active;

  const hasCFE =
    !!hasCouponConfigActive &&
    hasPermissions([Authorities.FISCAL_COUPON_GENERATE_IN_WORKORDER]);

  const hasNFE = !!invoiceSettingsByCompany?.issueNfe;
  const hasNFC = !!invoiceSettingsByCompany?.issueNfc;
  const hasNFS = !!invoiceSettingsByCompany?.issueNfs;

  const selectedRowKeys =
    complementsField
      ?.filter((complement) => complement.checked)
      .map((complement) => complement.id) || [];

  const modelTypeOptions = invoiceValuesModelsOptions
    .filter(
      (item) =>
        (item.invoiceType === InvoiceType.CFE && hasCFE) ||
        (item.invoiceType === InvoiceType.NFC && hasNFC) ||
        ((item.invoiceType === InvoiceType.NFE ||
          item.invoiceType === InvoiceType.NFS) &&
          (hasNFE || hasNFS)),
    )
    .map((item) => ({
      value: item.value,
      label: item.label,
    }));

  useEffect(() => {
    if (companyIdField) {
      setComplementCompanyId(companyIdField);
    }
  }, [companyIdField]);

  useEffect(() => {
    if (workorder) {
      form.setFieldsValue({
        companyId: workorder.company.id,
        complements: workorder.complements
          ?.filter(
            (complement) =>
              (hasPermissions([
                Authorities.MOVIMENTATION_WORKORDER_GENERATE_INVOICE_TO_AUTHORIZED,
              ]) &&
                complement.status === WorkOrderComplementStatus.Authorized) ||
              complement.status === WorkOrderComplementStatus.Closed,
          )
          .map((complement) => ({ ...complement, checked: false })),
      });
    }
  }, [workorder]);

  useEffect(() => {
    form.setFieldValue(
      'invoiceModelType',
      modelTypeOptions.length ? modelTypeOptions[0].value : undefined,
    );
  }, [invoiceSettingsByCompany, couponCompanyConfig.data]);

  return (
    <Card
      title="Selecione os complementos para gerar a nota fiscal"
      style={{ borderRadius: 10 }}
    >
      <Form form={form} onFinish={onSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              name="companyId"
              label="Empresa"
              rules={[fieldRuleRequired]}
              extra={
                companyIdField && !hasCouponConfigActive
                  ? 'Empresa sem configuração de cupom fiscal'
                  : undefined
              }
            >
              <CompaniesInfiniteSelectField
                disabled={
                  !matrix?.matrixSettings.generateInvoiceDifferentCompanies
                }
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="invoiceModelType"
              label="Modelo"
              rules={[fieldRuleRequired]}
            >
              <Select
                options={modelTypeOptions}
                loading={invoiceSettingsByCompanyIsLoading}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Typography.Title level={5}>Complementos</Typography.Title>
            <Form.List name="complements">
              {() => {
                function selectRowKeys(rowKeys: unknown) {
                  const keys = rowKeys as number[];

                  onChange();

                  form.setFieldValue(
                    'complements',
                    complementsField.map((complement) => ({
                      ...complement,
                      checked: keys.includes(complement.id),
                    })),
                  );
                }

                return (
                  <Table
                    dataSource={complementsField}
                    pagination={false}
                    scroll={{ x: true }}
                    rowKey={(row) => row.id}
                    rowSelection={{
                      selectedRowKeys,
                      onChange: selectRowKeys,
                    }}
                    columns={[
                      {
                        dataIndex: 'number',
                        title: 'Nº Complemento',
                        render: (complementNumber) =>
                          getWorkorderComplementNumber(
                            workorder?.number,
                            complementNumber,
                          ),
                      },
                      {
                        dataIndex: 'status',
                        title: 'Situação',
                        render: (status: WorkOrderComplementStatus) => (
                          <StatusTag
                            label={intl.formatMessage({
                              id: `workOrders.${status}`,
                            })}
                            color={workorderComplementColor[status]}
                          />
                        ),
                      },
                    ]}
                  />
                );
              }}
            </Form.List>
          </Col>

          <SpaceForButton justifyContent="end">
            <Button
              block
              type="primary"
              ghost
              style={{ marginTop: 24 }}
              disabled={!selectedRowKeys.length}
              htmlType="submit"
            >
              Analisar complementos
            </Button>
          </SpaceForButton>
        </Row>
      </Form>
    </Card>
  );
};

export default WorkOrderInvoiceComplements;
